"use client";

import type { LucideIcon } from "lucide-react";
import { Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { VOICE_WAVEFORM } from "@/lib/voice-waveform";
import { MARKETING_SCENARIOS } from "@/lib/assistant-scenarios";

type MiniWidgetProps = {
  icon: LucideIcon;
  label: string;
  value: string;
  note: string;
  className?: string;
};

function MiniWidget({ icon: Icon, label, value, note, className }: MiniWidgetProps) {
  return (
    <div className={cn("paper-tab rounded-2xl p-3", className)}>
      <div className="flex items-center gap-2">
        <span className="paper-tab flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
          <Icon className="h-4 w-4 text-foreground/80" aria-hidden="true" />
        </span>
        <span className="text-xs font-semibold text-foreground/70">{label}</span>
      </div>
      <p className="mt-3 font-display text-2xl font-extrabold tracking-tight text-foreground">
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p>
    </div>
  );
}

export function HeroCollage() {
  const [active, setActive] = useState(MARKETING_SCENARIOS[0]?.id ?? "book");
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(active);

  const scenario = useMemo(() => {
    return MARKETING_SCENARIOS.find((s) => s.id === active) ?? MARKETING_SCENARIOS[0];
  }, [active]);

  const isVoice = scenario.messageType === "voice";

  const activateScenario = useCallback(
    (id: (typeof MARKETING_SCENARIOS)[number]["id"], opts?: { interacted?: boolean }) => {
      const interacted = opts?.interacted === true;
      if (interacted) setHasInteracted(true);

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

      // Only show the "typing…" affordance for explicit user interaction (not auto-rotation),
      // otherwise the hero can briefly look empty/broken.
      if (interacted) {
        setIsTyping(true);
        setActive(id);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 900);
        return;
      }

      setIsTyping(false);
      setActive(id);
    },
    [],
  );

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    if (hasInteracted) return;
    if (typeof window !== "undefined") {
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      if (reduceMotion) return;
    }
    const ids = MARKETING_SCENARIOS.map((s) => s.id);
    const interval = setInterval(() => {
      const prev = activeRef.current;
      const idx = Math.max(0, ids.indexOf(prev));
      const next = ids[(idx + 1) % ids.length] ?? ids[0] ?? "book";
      activateScenario(next);
    }, 7200);
    return () => clearInterval(interval);
  }, [activateScenario, hasInteracted]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  return (
    <div className="relative mx-auto w-full max-w-[520px] sm:max-w-[560px]">
      <div className="paper-tab relative aspect-[6/7] overflow-hidden rounded-[2.5rem] p-6 sm:p-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.9]"
          style={{
            background:
              "radial-gradient(980px circle at 14% 8%, color-mix(in srgb, var(--primary) 10%, transparent), transparent 58%), radial-gradient(820px circle at 88% 0%, color-mix(in srgb, var(--primary) 6%, transparent), transparent 62%), linear-gradient(180deg, rgba(255,255,255,0.78), rgba(255,255,255,0.22))",
          }}
        />

        <div className="relative flex h-full flex-col">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-foreground/15" />
                <span className="h-2 w-2 rounded-full bg-foreground/10" />
                <span className="h-2 w-2 rounded-full bg-foreground/10" />
              </div>
              <span className="text-xs font-semibold tracking-tight text-foreground/70">
                Dobbix
              </span>
            </div>
          </div>

          <div className="mt-5">
            <div
              className="segmented-shell inline-flex gap-1 rounded-full p-1"
              onMouseEnter={() => setHasInteracted(true)}
              onFocusCapture={() => setHasInteracted(true)}
            >
              {MARKETING_SCENARIOS.map((s) => {
                const isActive = s.id === active;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      activateScenario(s.id, { interacted: true });
                    }}
                    aria-pressed={isActive}
                    className={cn(
                      "segmented-item rounded-full px-3 py-1.5 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
                      isActive ? "segmented-item-active text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex-1 space-y-3">
            <div
              key={`you-${scenario.id}`}
              className="max-w-[90%] rounded-3xl border border-border/70 bg-background/75 px-5 py-4 scenario-messages"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="text-[11px] font-semibold text-foreground/55">You</p>
                <span className="stamp rounded-full px-3 py-1 text-[11px] font-semibold text-foreground/60">
                  {isVoice ? "Voice note" : "Text"}
                </span>
              </div>

              {isVoice ? (
                <>
                  <div className="mt-3 flex items-center gap-3">
                    <span className="paper-tab flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl">
                      <Play className="h-4 w-4 text-foreground/80" aria-hidden="true" />
                    </span>
                    <div className="flex items-end gap-0.5" aria-hidden="true">
                      {VOICE_WAVEFORM.map((height, idx) => (
                        <span
                          key={idx}
                          className="waveform-bar w-1 rounded-full bg-primary/55"
                          style={{
                            height: `${height}px`,
                            animationDelay: `${idx * 70}ms`,
                          }}
                        />
                      ))}
                    </div>
                    <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                      {scenario.voiceDuration ?? "0:00"}
                    </span>
                  </div>

                  <div className="mt-4 rounded-2xl border border-border/60 bg-background/60 px-4 py-3">
                    <p className="text-xs font-semibold text-foreground/70">Transcript</p>
                    <p className="mt-1 text-sm font-medium text-foreground">{scenario.message}</p>
                  </div>
                </>
              ) : (
                <p className="mt-2 text-sm font-medium text-foreground">{scenario.message}</p>
              )}

              <p className="mt-2 text-xs text-muted-foreground">{scenario.hero.youNote}</p>
            </div>

            <div
              key={`assistant-${scenario.id}`}
              className="ml-auto max-w-[96%] rounded-3xl border border-primary/[0.14] bg-primary/[0.06] px-5 py-4"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="text-[11px] font-semibold text-foreground/55">Dobbix</p>
                <span className="inline-flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary/50" aria-hidden="true" />
                  Actions
                </span>
              </div>

              <div className="mt-4 grid gap-2">
                {isTyping ? (
                  <div className="inline-flex w-fit items-center gap-1.5 rounded-2xl border border-border/70 bg-background/60 px-3 py-2">
                    <span
                      className="typing-dot h-1.5 w-1.5 rounded-full bg-foreground/45"
                      style={{ animationDelay: "0ms" }}
                    />
                    <span
                      className="typing-dot h-1.5 w-1.5 rounded-full bg-foreground/45"
                      style={{ animationDelay: "180ms" }}
                    />
                    <span
                      className="typing-dot h-1.5 w-1.5 rounded-full bg-foreground/45"
                      style={{ animationDelay: "360ms" }}
                    />
                  </div>
                ) : (
                  <div className="scenario-messages grid gap-2">
                    {scenario.hero.actions.map((a, idx) => {
                      const Icon = a.icon;
                      const isPrimary = a.tone === "primary";
                      return (
                        <div
                          key={a.label}
                          className={cn(
                            "animate-fade-in-up flex items-center gap-2 rounded-2xl border px-3 py-2",
                            isPrimary
                              ? "border-primary/20 bg-primary/[0.05]"
                              : "border-border/70 bg-background/60",
                          )}
                          style={{ animationDelay: `${idx * 70}ms` }}
                        >
                          <Icon
                            className={cn(
                              "h-4 w-4",
                              isPrimary ? "text-primary" : "text-foreground/70",
                            )}
                            aria-hidden="true"
                          />
                          <span className="text-xs font-semibold text-foreground/80">
                            {a.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary/40" aria-hidden="true" />
                {scenario.hero.assistantNote}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {scenario.hero.widgets.map((w) => (
              <MiniWidget
                key={w.label}
                icon={w.icon}
                label={w.label}
                value={w.value}
                note={w.note}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
